import { useTranslation } from 'react-i18next';
import useAsync from 'react-use/lib/useAsync';
import { useQuery } from '@tanstack/react-query';
import CustomObjectsService from '../services/CustomObjectsService';
import { FORCE_ALL_RECORDS_SIZE } from '../services/helpers';
import { integrationSecretsQuery } from 'query/integration-secrets';
import { capitalize } from 'lodash/string';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import { getBusinessMergeFields } from 'components/WYSIWYG/use-merge-fields';
import { FIELD_TYPES } from 'utility/constants';
import { isMergeFieldUnescaped } from 'utility/merge-fields';
import {
  getAutomationVariablesCategoryName,
  automationVariable,
  firstField,
} from './mergeVariableHelpers';

const EMPTY_ARRAY = [];

export const getSystemFieldsCategoryName = (t) => t('System Fields');
export const getBusinessFieldsCategoryName = (t) => t('Business Fields');
const decorateOption = (field, relationship, labelPostfix, valuePostfix) => ({
  label: `${field.displayName} (${labelPostfix})`,
  value: `{{ ${relationship}.${valuePostfix} }}`,
  relationship: `${relationship}.${valuePostfix}`,
  fieldType: field.fieldType,
});

const automationHistory = (name, displayName, baseField) => ({
  ...baseField,
  name,
  id: `automation_history.${name}`,
  isDefault: false,
  displayName,
  fieldPrefix: 'automation_history',
});

export const useMergeFields = ({
  customObjectClassName,
  alternativeNaming = '',
  customObject,
  entityName,
  addAutomationHistory = false,
  addIntegrationSecrets = false,
  automationVariables = [],
  addAdditionalOptions = false,
  isGlobalAutomation = false,
  excludeBusinessFields = false,
}) => {
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);

  const { data: integrationSecrets, isError: integrationSecretsError } =
    useQuery(
      integrationSecretsQuery({
        enabled: addIntegrationSecrets,
        params: { page_size: FORCE_ALL_RECORDS_SIZE },
        select: ({ results }) => results,
      })
    );

  const secretsReady =
    !addIntegrationSecrets || !!integrationSecrets || integrationSecretsError;

  const { loading = true, value: mergeFields } = useAsync(async () => {
    if (!secretsReady) {
      return undefined;
    }

    const { categorizedFields } = isGlobalAutomation
      ? { categorizedFields: [] }
      : await CustomObjectsService.getCategorizedModelFields(customObject.id, {
          settingsSearch: true,
        });

    // Specific case, when we need id as a merged file for sending requests
    const systemFields = {
      order: 0,
      id: uuidv4(),
      name: getSystemFieldsCategoryName(t),
      fields: [
        !isGlobalAutomation && {
          ...firstField,
          name: 'id',
          id: '_id_',
          isDefault: false,
          displayName: `${capitalize(
            entityName.endsWith('s') ? entityName.slice(0, -1) : entityName
          )} ID`,
        },
        addAutomationHistory && {
          ...automationHistory(
            'automation_id',
            t('Agentic Workflow ID'),
            firstField
          ),
        },
        addAutomationHistory && {
          ...automationHistory(
            'execution_id',
            t('Agentic Workflow Execution ID'),
            firstField
          ),
        },
      ].filter(Boolean),
    };

    let extendedFields = [systemFields];

    if (automationVariables.length) {
      const variableFields = {
        order: 1,
        id: uuidv4(),
        name: getAutomationVariablesCategoryName(t),
        fields: automationVariables.reduce(
          (prev, { label, value, meta }) => [
            ...prev,
            ...automationVariable(
              value,
              label,
              meta,
              firstField,
              chosenBusiness?.client_object?.id,
              t
            ),
          ],
          []
        ),
      };
      extendedFields.push(variableFields);
    }

    const secretFields = integrationSecrets?.length
      ? {
          order: 1,
          id: uuidv4(),
          name: t('Integration Secrets'),
          fields: integrationSecrets.map((secret) => ({
            ...firstField,
            name: secret.api_name,
            id: `integration_secret.${secret.api_name}`,
            isDefault: false,
            displayName: secret.description,
            relationship: `integration_secret.${secret.api_name}`,
          })),
        }
      : null;

    const businessFields = {
      order: categorizedFields.length + automationVariables.length ? 2 : 1,
      id: uuidv4(),
      name: getBusinessFieldsCategoryName(t),
      fields: getBusinessMergeFields(
        t,
        chosenBusiness.business_variables,
        excludeBusinessFields
      ).map(({ label, relationship }) => ({
        ...firstField,
        name: relationship.split('.').pop(),
        id: relationship,
        isDefault: false,
        displayName: label,
        relationship,
      })),
    };

    extendedFields = extendedFields.concat(
      ...[secretFields, categorizedFields, businessFields].filter(Boolean)
    );

    const getMergeFieldValue = (field) => {
      if (field.isDefault) {
        return `${alternativeNaming || customObjectClassName}.${field.name}`;
      }

      if (field.relationship) {
        return field.relationship;
      }

      return `${
        field.fieldPrefix || alternativeNaming || customObjectClassName
      }.${field.name}`;
    };

    // TODO Try re-adding city and country fields?
    const categorizedFieldOptions = extendedFields.map(
      ({ name, fields, id }) => {
        return {
          key: id,
          label: name,
          items: fields.reduce((prev, field) => {
            const relationship = getMergeFieldValue(field);
            if (
              addAdditionalOptions &&
              field.fieldType === FIELD_TYPES.Relationship.type
            ) {
              prev.push(decorateOption(field, relationship, 'ID', 'id'));
              if (
                field?.relation?.relatedObjectObjectName !== 'client_client'
              ) {
                prev.push(
                  ...[
                    decorateOption(field, relationship, 'Name', 'name'),
                    decorateOption(
                      field,
                      relationship,
                      'Display Name',
                      'display_name'
                    ),
                  ]
                );
              } else {
                prev.push(
                  ...[
                    decorateOption(
                      field,
                      relationship,
                      'Full Name',
                      'full_name'
                    ),
                    decorateOption(field, relationship, 'Email', 'email'),
                  ]
                );
              }
            } else if (field.fieldType === FIELD_TYPES.TeamSelector.type) {
              prev.push(
                ...[
                  decorateOption(field, relationship, 'ID', 'id'),
                  decorateOption(field, relationship, 'Full Name', 'full_name'),
                  decorateOption(field, relationship, 'Email', 'email'),
                ]
              );
            } else if (field.fieldType === FIELD_TYPES.PhoneNumber.type) {
              prev.push(
                ...[
                  decorateOption(field, relationship, 'e164', 'e164'),
                  decorateOption(field, relationship, 'Local Format', 'local'),
                ]
              );
            } else {
              prev.push({
                label: field.displayName,
                value: `{{ ${relationship} }}`,
                relationship,
                unescaped: isMergeFieldUnescaped(field),
                fieldType: field.fieldType,
              });
            }
            return prev;
          }, []),
        };
      }
    );

    return categorizedFieldOptions;
  }, [secretsReady]);

  return {
    mergeFields: mergeFields || EMPTY_ARRAY,
    loading: loading || !mergeFields || !secretsReady,
  };
};
